import React from "react";
import { X, Check, Minus } from "lucide-react";
import { fmtPrice } from "../../lib/symbols";

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const signed = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}`);
const ts = (t) => new Date(t * 1000).toISOString().slice(11, 19);
const STATE_TONE = { SUCCESS: "text-buy", FAILED: "text-sell", HEDGED: "text-primary", ACTIVE: "text-[#34D399]", CONFIRMED: "text-foreground", PENDING: "text-muted-foreground", IDLE: "text-muted-foreground" };

// T50 strategy card block (informational — confluence weights untouched). Rendered inside the Confluence card.
export function T50StrategyRow({ t50 }) {
  if (!t50) return null;
  const last = t50.last, pend = t50.pending;
  const running = last && last.state === "ACTIVE";
  return (
    <div className="pb-1 mb-1 border-b border-border/60 space-y-0.5" data-testid="confluence-row-t50" title="Informational — T50 does not alter the 100-point score">
      <div className="flex items-center gap-2">
        {last && /SUCCESS|ACTIVE|CONFIRMED/.test(last.state) ? <Check className={`h-3 w-3 ${last.dir === "bull" ? "text-buy" : "text-sell"}`} /> : <Minus className="h-3 w-3 text-muted-foreground" />}
        <span className="flex-1">T50 <span className="text-muted-foreground">{Math.round(t50.config.threshold * 100)}%{t50.mode === "tick" ? ` · tick w${t50.config.microWindow}` : ""}</span></span>
        <span className={`truncate max-w-[130px] ${STATE_TONE[t50.state] || ""}`} data-testid="confluence-t50-state">{last ? `${last.dir === "bull" ? "bullish" : "bearish"} · ${last.state.toLowerCase()}` : pend ? "waiting for bar 2…" : "idle"}</span>
        <span className="font-mono-nums w-12 text-right text-muted-foreground">info</span>
      </div>
      {last && (
        <div className="pl-5 text-[10px] text-muted-foreground font-mono-nums flex flex-wrap gap-x-2" data-testid="confluence-t50-detail">
          <span>close loc {pct(last.closeLoc)}</span>
          {last.entryPrice != null && <span>eval {last.ticks}/{t50.config.window}{running ? " ticks…" : ""}</span>}
          {last.entryPrice != null && <span className="text-buy">MFE {signed(last.mfe)}</span>}
          {last.entryPrice != null && <span className="text-sell">MAE {signed(-last.mae)}</span>}
        </div>
      )}
      {!last && pend && t50.decidableBars > 0 && <div className="pl-5 text-[10px] text-muted-foreground">Bar 2 forming · close location {pct(pend.closeLoc)}</div>}
      {!last && t50.decidableBars === 0 && <div className="pl-5 text-[10px] text-muted-foreground" data-testid="confluence-t50-note">collecting ticks…</div>}
    </div>
  );
}

// Detail card shown when a T50 marker is clicked on the chart.
export function T50DetailCard({ ev, decimals = 2, onClose }) {
  if (!ev) return null;
  const p = (v) => fmtPrice(v, decimals);
  const rows = [
    ["Direction", <span className={ev.dir === "bull" ? "text-buy" : "text-sell"}>{ev.dir === "bull" ? "Bullish" : "Bearish"}</span>],
    ["State", <span className={STATE_TONE[ev.state]}>{ev.state}</span>],
    ["Mode", ev.mode === "tick" ? `Tick-T50 · ${ev.microWindow}-tick micro-bar` : "Bar T50 (chart candles)"],
    ["Threshold", `${Math.round(ev.threshold * 100)}%`],
    [ev.mode === "tick" ? "Micro-bar 1" : "Bar 1", ts(ev.bar1.time)], [ev.mode === "tick" ? "Micro-bar 2" : "Bar 2", ts(ev.bar2.time)],
    ["Bar 2 O / H", `${p(ev.bar2.open)} / ${p(ev.bar2.high)}`], ["Bar 2 L / C", `${p(ev.bar2.low)} / ${p(ev.bar2.close)}`],
    ["Close location", pct(ev.closeLoc)],
    ["Body / range", pct(ev.displacement)],
    ["Entry", ev.entryPrice != null ? `${p(ev.entryPrice)} @ ${ts(ev.entryTime)}` : "awaiting next tick"],
    ["Evaluation", `${ev.ticks} ticks${ev.complete ? "" : " (running)"}`],
    ["MFE", <span className="text-buy">{signed(ev.mfe)} · tick {ev.mfeTick || "—"}</span>],
    ["MAE", <span className="text-sell">{signed(-ev.mae)} · tick {ev.maeTick || "—"}</span>],
    ["Failure", ev.failed ? <span className="text-sell">next bar closed opposite</span> : "no"],
    ...(ev.hedge ? [["Hedge", <span className="text-primary">{ev.hedge.dir.toUpperCase()} @ {p(ev.hedge.entryPrice)} · MFE {signed(ev.hedge.mfe)} · {ev.hedge.recovered ? "recovered" : "not recovered"}</span>]] : []),
    ["Outcome", <span className={STATE_TONE[ev.outcome] || ""}>{ev.outcome}</span>],
  ];
  return (
    <div className="absolute left-3 top-16 z-30 w-72 rounded-xl border border-border bg-panel/95 backdrop-blur-md shadow-xl text-xs select-none" data-testid="t50-detail-card">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <span className="font-bold tracking-widest text-primary">T50</span>
        <span className="text-muted-foreground">two-bar decision</span>
        <button data-testid="t50-detail-close" onClick={onClose} className="ml-auto h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
      </div>
      <div className="px-3 py-2 grid grid-cols-[110px_1fr] gap-y-0.5 font-mono-nums">
        {rows.map(([k, v]) => <React.Fragment key={k}><span className="text-muted-foreground">{k}</span><span className="text-right truncate">{v}</span></React.Fragment>)}
      </div>
      <div className="px-3 pb-2 flex flex-wrap gap-1" data-testid="t50-detail-targets">
        {ev.targets.map((t) => <span key={t.target} className={`px-1.5 py-0.5 rounded text-[10px] font-mono-nums ${t.hit ? "bg-buy/15 text-buy" : "bg-secondary text-muted-foreground"}`}>+{t.target} {t.hit ? `✓ t${t.tick}` : "—"}</span>)}
      </div>
    </div>
  );
}
